import {FileClient} from '@/modules/files/files_module';
import {generatePeaksFromAudioSrcUrl} from '@/utils/generate_waveform_data';
import {isAudioFile} from '@/utils/file_utils';

import {AttributeName, AttributeParentType, DB} from '@/store/kysely_types';
import {Kysely} from 'kysely';
import {generateId} from '@/store/utils/generate_id';
import {DBObjects} from '@/store/store_types';
import {DBService} from '@/store/actions/actions';

type Props = {
    db: Kysely<DB>;
}

export default async function (props: Props) {
    await runMigration(props);
}

const runMigration = async ({db}: Props) => {
    const workspaces = await getAllWorkspaces({db});

    for (const workspace of workspaces) {
        const files = await getFilesForWorkspace({db, workspace_id: workspace.id});

        for (const file of files) {
            const fileRevisions = await getFileRevisions({db, file_id: file.id});
            if (fileRevisions.length !== 0) {
                continue;
            }

            const fileRevisionId = generateId();
            await db.insertInto('FileRevision').values({
                id: fileRevisionId,
                file_id: file.id,
                created_by: file.created_by,
                revision_num: 1,
                revision_name: 'v1',
                workspace_id: workspace.id,
            }).execute();

            await db.updateTable('RawFile')
                .set({
                    file_revision_id: fileRevisionId,
                })
                .where('RawFile.file_id', '=', file.id)
                .execute();

            const project = await db.selectFrom('Attachment')
                .where('Attachment.child_id', '=', file.id)
                .where('Attachment.child_type', '=', 'File')
                .where('Attachment.parent_type', '=', 'Project')
                .select('parent_id as id')
                .executeTakeFirst();

            if (project) {
                const dbService = new DBService(db, file.created_by);
                await dbService.files.addToProject(file.id, project.id);
            }
        }
    }
};

const getAllWorkspaces = async (args: {
    db: Kysely<DB>;
}) => {
    const db = args.db;

    const workspaces = await db.selectFrom('Workspace')
        .selectAll('Workspace')
        .execute();

    return workspaces;
};

const getFilesForWorkspace = async (args: {
    db: Kysely<DB>;
    workspace_id: string;
}) => {
    const db = args.db;

    const files = await db.selectFrom('File')
        .innerJoin('RawFile', 'RawFile.file_id', 'File.id')
        .selectAll('File')
        .select('RawFile.full_path')
        .where('File.workspace_id', '=', args.workspace_id)
        .execute();

    return files;
};

const getFileRevisions = async (args: {
    db: Kysely<DB>;
    file_id: string;
}) => {
    const db = args.db;

    const fileRevisions = await db.selectFrom('FileRevision')
        .selectAll('FileRevision')
        .where('FileRevision.file_id', '=', args.file_id)
        .execute();

    return fileRevisions;
};
